import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Clock, Crown, Gem, Trophy, User, XCircle } from 'lucide-react'
import confetti from 'canvas-confetti'
import useRushdropStore from '../store/useRushdropStore'
import useAppStore from '../../../store/useAppStore'
import RushDropWeeklyDrawing from '../components/RushDropWeeklyDrawing'
import RushDropWeeklyTop5 from '../components/RushDropWeeklyTop5'
import Loader from '../../../components/Loader'
import useCountdown from '../hooks/useCountdown'
import useAudioManager from '../../../hooks/useAudioManager'
import { formatClockTimer } from '../utils/rushdropFormat'
import { formatNumber, formatPrice, parseSkinName } from '../../../utils/formatters'
import { hapticImpact, hapticNotify } from '../../../utils/haptic'
import ProfileLink from '../../../components/ProfileLink'
import TeamTag from '../../../components/TeamTag'

// Поллинг по стадиям: announce — данные меняются медленно,
// drawing — чаще, чтобы не задерживать показ победителя,
// results — данные заморожены, достаточно редкого опроса
const POLL_INTERVALS = {
  announce: 15000,
  drawing: 4000,
  results: 30000
}

/**
 * Экран недельного розыгрыша Rush Drop (GET /api/fast-event/weekly/).
 * Стадии рисуются строго по state: announce | drawing | results.
 * Таймеры тикают локально (useCountdown) с ресинком от поллинга;
 * на нуле таймера жеребьёвки — «идёт розыгрыш» и внеочередной запрос к API.
 * { enabled: false } — штатный ответ вне окна: экран просто закрывается,
 * заглушку «розыгрыш скоро» не рисуем.
 */
const RushDropWeeklyDraw = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { weeklyData, weeklyLoading, weeklyError, loadWeekly } = useRushdropStore()
  const language = useAppStore((state) => state.language)
  const isGuest = useAppStore((state) => state.isGuest)
  const { playSound } = useAudioManager({ select: 'select', win: 'win' })
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false)
  const prevLanguageRef = useRef(language)
  const confettiFiredRef = useRef(false)

  const isEnabled = Boolean(weeklyData) && weeklyData.enabled !== false
  const state = isEnabled ? weeklyData.state : null

  const closeScreen = () => navigate('/', { replace: true })

  // Первичная загрузка + мгновенный рефетч при возврате во вкладку
  useEffect(() => {
    loadWeekly()

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadWeekly({ silent: true })
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadWeekly, isGuest])

  // Поллинг с периодом под текущую стадию
  useEffect(() => {
    if (!state) return undefined
    const interval = setInterval(() => {
      loadWeekly({ silent: true })
    }, isGuest ? Math.max(10000, POLL_INTERVALS[state] || 15000) : POLL_INTERVALS[state] || 15000)
    return () => clearInterval(interval)
  }, [state, loadWeekly, isGuest])

  // Тексты приходят на языке профиля — перезапрашиваем после смены языка
  useEffect(() => {
    if (prevLanguageRef.current !== language) {
      prevLanguageRef.current = language
      loadWeekly({ silent: true })
    }
  }, [language, loadWeekly])

  // Нет окна розыгрыша — экран открывать не с чего, закрываем без заглушек
  useEffect(() => {
    if (weeklyData && weeklyData.enabled === false) {
      closeScreen()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyData])

  // Локальный таймер жеребьёвки (только в announce; в drawing/results там 0)
  const drawRemaining = useCountdown(
    state === 'announce' ? weeklyData?.draw_in_seconds : null
  )

  // Дошло до нуля — не показываем «0»: внеочередной опрос API,
  // сервер переключит state на drawing
  useEffect(() => {
    if (state === 'announce' && drawRemaining === 0) {
      const timeout = setTimeout(() => loadWeekly({ silent: true }), 1000)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [state, drawRemaining, loadWeekly])

  // Локальный таймер закрытия результатов; на нуле экран закрывается
  const resultsRemaining = useCountdown(
    state === 'results' ? weeklyData?.results_in_seconds : null
  )
  useEffect(() => {
    if (
      state === 'results' &&
      weeklyData?.results_in_seconds != null &&
      resultsRemaining === 0
    ) {
      closeScreen()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, resultsRemaining, weeklyData?.results_in_seconds])

  // «Ты выиграл» — конфетти, звук и вибрация один раз при появлении результатов
  useEffect(() => {
    if (
      state === 'results' &&
      Number(weeklyData?.winner?.you) === 1 &&
      !confettiFiredRef.current
    ) {
      confettiFiredRef.current = true
      playSound('win')
      hapticNotify('success')
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00FF7F', '#4EFFC6', '#FFD700']
      })
    }
  }, [state, weeklyData?.winner?.you, playSound])

  // Первая загрузка
  if (weeklyLoading && !weeklyData) {
    return <Loader message="" />
  }

  // Ошибка без данных
  if (weeklyError && !weeklyData) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-sm text-text-secondary">
          {t('rushdrop.error', 'Не удалось загрузить Rush Drop')}
        </p>
        <button
          type="button"
          onClick={() => loadWeekly()}
          className="rounded-xl bg-gradient-to-r from-accent-primary to-accent-secondary px-6 py-2.5 text-sm font-semibold text-dark-primary"
        >
          {t('rushdrop.retry', 'Попробовать снова')}
        </button>
      </div>
    )
  }

  // Окно закрылось (или не наступило) — рендерим ничего, эффект выше уведёт на главную
  if (!isEnabled) {
    return <Loader message="" />
  }

  const {
    skin,
    crystals,
    banner,
    title,
    description,
    participants,
    top5,
    you,
    winner
  } = weeklyData

  const skinName = parseSkinName(skin?.name)
  // В drawing и results title/description приходят null — заголовок там свой
  const heading = title || (state === 'drawing'
    ? t('rushdrop.weeklyDraw.drawing', 'Идёт розыгрыш')
    : t('rushdrop.weeklyDraw.resultsTitle', 'Итоги розыгрыша недели'))

  const renderPrize = (prize) => {
    if (prize?.kind === 'skin' && prize?.skin) {
      const prizeSkin = parseSkinName(prize.skin.name)
      return (
        <span className="flex min-w-0 items-center gap-2">
          {prize.skin.image && (
            <img
              src={prize.skin.image}
              alt={prize.skin.name || ''}
              className="h-8 w-8 shrink-0 object-contain"
              loading="lazy"
            />
          )}
          <span className="truncate text-xs font-medium text-white">
            {prizeSkin.title || prize.skin.name}
          </span>
        </span>
      )
    }
    return (
      <span className="flex items-center gap-1 text-sm font-bold tabular-nums text-accent-primary">
        <Gem size={14} />
        {formatPrice(prize?.crystals)}
      </span>
    )
  }

  const renderWinnerBlock = () => {
    // Приз сгорел: розыгрыш состоялся, но за неделю ни у кого не было баллов
    if (!winner) {
      return (
        <section className="rounded-2xl border border-white/10 bg-surface p-6">
          <div className="flex min-h-[120px] flex-col items-center justify-center gap-3 text-center">
            <XCircle size={28} className="text-text-secondary" />
            <p className="font-semibold text-white">
              {t('rushdrop.weeklyDraw.burned', 'Победителя нет — приз сгорел')}
            </p>
            <p className="text-xs text-text-secondary">
              {t('rushdrop.weeklyDraw.burnedHint', 'За неделю никто не набрал ни одного балла')}
            </p>
          </div>
        </section>
      )
    }

    const isMe = Number(winner.you) === 1
    const isCrystals = winner.prize?.kind === 'crystals'

    return (
      <section
        className={`rounded-2xl border p-4 ${
          isMe
            ? 'border-accent-primary/40 bg-accent-primary/10'
            : 'border-yellow-400/30 bg-gradient-to-br from-yellow-400/10 to-orange-500/5'
        }`}
      >
        <div className="flex items-center gap-2">
          <Trophy size={16} className={isMe ? 'text-accent-primary' : 'text-yellow-400'} />
          <span className={`text-[11px] font-bold uppercase tracking-wider ${isMe ? 'text-accent-primary' : 'text-yellow-400'}`}>
            {isMe
              ? t('rushdrop.weeklyDraw.youWon', 'Ты выиграл!')
              : t('rushdrop.weeklyDraw.winner', 'Победитель')}
          </span>
        </div>

        <div className="mt-3 flex items-center gap-3">
          {/* Аватар и имя победителя кликабельны: slug === null → не кликабелен */}
          <ProfileLink slug={winner.slug} className="shrink-0">
            {winner.avatar ? (
              <img
                src={winner.avatar}
                alt=""
                className="h-12 w-12 rounded-full border-2 border-yellow-400/50 object-cover"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5">
                <User size={20} className="text-text-secondary" />
              </div>
            )}
          </ProfileLink>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-white">
              <TeamTag team={winner.team} className="" />
              <ProfileLink slug={winner.slug}>
                {winner.name || t('rushdrop.leaderboard.player', 'Игрок')}
              </ProfileLink>
            </p>
            <p className="text-xs tabular-nums text-text-secondary">
              {formatNumber(Number(winner.points || 0))} {t('rushdrop.weeklyDraw.points', 'баллов')}
              {' · '}
              {t('rushdrop.weeklyDraw.chance', 'Шанс')}{' '}
              {Number(winner.chance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%
            </p>
          </div>
          {renderPrize(winner.prize)}
        </div>

        {/* Приз выдаётся в одной транзакции с жеребьёвкой — к показу результатов уже у победителя */}
        <p className="mt-3 text-xs text-text-secondary">
          {isCrystals
            ? t('rushdrop.weeklyDraw.crystalsSent', 'Предмет пропал из каталога — вместо него начислены кристаллы на баланс')
            : isMe
              ? t('rushdrop.weeklyDraw.prizeInInventory', 'Приз уже в твоём инвентаре')
              : t('rushdrop.weeklyDraw.prizeSent', 'Приз отправлен победителю')}
        </p>
      </section>
    )
  }

  return (
    <div className="mx-auto max-w-[720px] space-y-4 px-4 py-4 lg:px-10 lg:py-6">
      {/* Шапка: баннер недели ("" — пула нет, рисуем свой фон) */}
      <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-surface">
        <div className="relative aspect-[16/9]">
          {banner ? (
            <img
              src={banner}
              alt={heading}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-yellow-400/20 via-surface to-orange-500/15">
              <Crown size={56} className="text-yellow-400/70" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />

          <div className="absolute bottom-3 left-4 right-4">
            <span className="mb-1.5 inline-flex items-center gap-1 rounded-md bg-yellow-400 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-dark-primary">
              <Crown size={12} />
              {t('rushdrop.weeklyDraw.badge', 'Розыгрыш недели')}
            </span>
            <h1 className="text-2xl font-black not-italic leading-tight text-white lg:text-3xl">
              {heading}
            </h1>
          </div>
        </div>
      </div>

      {/* Приз недели */}
      <section className="flex items-center gap-3 rounded-2xl border border-white/10 bg-surface p-4">
        {skin?.image ? (
          <img
            src={skin.image}
            alt={skin.name || ''}
            className="h-20 w-20 shrink-0 object-contain"
            loading="lazy"
          />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-white/5">
            <Crown size={28} className="text-yellow-400/60" />
          </div>
        )}
        <div className="min-w-0">
          <p className="text-sm font-semibold leading-snug text-white">
            {skinName.title || skin?.name}
          </p>
          {skinName.subtitleCompact && (
            <p className="mt-0.5 text-[11px] text-text-secondary">{skinName.subtitleCompact}</p>
          )}
          {skin?.type && (
            <p className="mt-0.5 text-[11px] text-text-secondary">{skin.type}</p>
          )}
          <p className="mt-1 flex items-center gap-1 text-xs tabular-nums text-text-secondary">
            <Gem size={12} className="text-accent-primary" />
            ≈ {formatPrice(crystals)}
          </p>
        </div>
      </section>

      {state === 'announce' && (
        <>
          {/* Таймер жеребьёвки: локальный тик, на нуле — «идёт розыгрыш» */}
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/10 to-orange-500/5 px-4 py-6">
            <span className="flex items-center gap-2 text-sm text-text-secondary">
              <Clock size={16} className="text-yellow-400" />
              {t('rushdrop.weeklyDraw.drawIn', 'До жеребьёвки')}
            </span>
            <span className="text-5xl font-black tabular-nums text-yellow-300">
              {drawRemaining > 0
                ? formatClockTimer(drawRemaining)
                : t('rushdrop.weeklyDraw.drawing', 'Идёт розыгрыш')}
            </span>
          </div>

          <RushDropWeeklyTop5 top5={top5} you={you} participants={participants} />

          {/* Правила розыгрыша — длинный текст под раскрытием */}
          {description && (
            <div className="rounded-2xl border border-white/10 bg-surface p-4">
              <button
                type="button"
                onClick={() => {
                  hapticImpact('light')
                  setIsDescriptionOpen((prev) => !prev)
                }}
                className="flex items-center gap-1 text-xs font-semibold text-yellow-300 transition-all active:scale-[0.98]"
              >
                {isDescriptionOpen
                  ? t('rushdrop.weekly.less', 'Скрыть')
                  : t('rushdrop.weeklyDraw.more', 'Как это работает')}
                <ChevronDown
                  size={14}
                  className={`transition-transform ${isDescriptionOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isDescriptionOpen && (
                <p className="mt-2 whitespace-pre-line text-xs leading-relaxed text-text-secondary">
                  {description}
                </p>
              )}
            </div>
          )}
        </>
      )}

      {state === 'drawing' && (
        <RushDropWeeklyDrawing skin={skin} />
      )}

      {state === 'results' && (
        <>
          {renderWinnerBlock()}

          <RushDropWeeklyTop5 top5={top5} you={you} participants={participants} />

          {resultsRemaining > 0 && (
            <p className="flex items-center justify-center gap-1.5 text-center text-xs text-text-secondary">
              <Clock size={13} className="text-yellow-400" />
              {t('rushdrop.weeklyDraw.closeIn', 'Экран закроется через')}{' '}
              <b className="tabular-nums text-yellow-300">{formatClockTimer(resultsRemaining)}</b>
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default RushDropWeeklyDraw
